import { Line } from '@react-three/drei';
import React, { memo, useMemo } from 'react';
import * as THREE from 'three';

import { PreviewWallProps } from '../../@types/Components';
import PreviewWallModel from '../../models/standard/PreviewWallModel';
import { UNIT_TO_METER_DIVISOR } from '../../utils/constants';
import RENDER_ORDERS from '../../utils/renderOrder';
import InnerWallLengthIndicator from './indicators/InnerWallLengthIndicator';
import OuterWallLengthIndicator from './indicators/OuterWallLengthIndicator';
import WallLengthIndicator from './indicators/WallLengthIndicator';

const PreviewWall = memo(({ wall, isIntersecting }: PreviewWallProps): React.JSX.Element | null => {
  const meshColor = useMemo(() => (isIntersecting ? '#ff0000' : '#888888'), [isIntersecting]);

  const wallData = useMemo(() => {
    if (!wall || wall.startPoint.equals(wall.endPoint)) return null;

    let ptC: THREE.Vector2;
    let ptD: THREE.Vector2;
    let ptC2: THREE.Vector2;
    let ptD2: THREE.Vector2;

    if (PreviewWallModel.isPreviewWall(wall)) {
      ptC = new THREE.Vector2(wall.pointC.x, wall.pointC.y);
      ptD = new THREE.Vector2(wall.pointD.x, wall.pointD.y);
      ptC2 = new THREE.Vector2(wall.pointC2.x, wall.pointC2.y);
      ptD2 = new THREE.Vector2(wall.pointD2.x, wall.pointD2.y);
    } else {
      const start = wall.startPoint;
      const end = wall.endPoint;
      const depth = wall.depth;

      const dir = new THREE.Vector2(end.x - start.x, end.y - start.y);
      const len = dir.length();
      if (len < 1e-6) return null;

      dir.divideScalar(len);

      const normal = new THREE.Vector2(-dir.y, dir.x).multiplyScalar(depth / 2);

      ptC = new THREE.Vector2(start.x + normal.x, start.y + normal.y);
      ptD = new THREE.Vector2(end.x + normal.x, end.y + normal.y);
      ptC2 = new THREE.Vector2(start.x - normal.x, start.y - normal.y);
      ptD2 = new THREE.Vector2(end.x - normal.x, end.y - normal.y);
    }

    const shape = new THREE.Shape();
    shape.moveTo(ptC.x, -ptC.y);
    shape.lineTo(ptD.x, -ptD.y);
    shape.lineTo(ptD2.x, -ptD2.y);
    shape.lineTo(ptC2.x, -ptC2.y);
    shape.closePath();

    const geometry = new THREE.ShapeGeometry(shape);
    geometry.rotateX(Math.PI / 2);

    return { geometry, ptC, ptD, ptC2, ptD2 };
  }, [wall]);

  const labelPoints = useMemo(() => {
    if (!wall) return null;

    if (PreviewWallModel.isPreviewWall(wall)) {
      const displayInnerStartPoint =
        (wall as any).displayInnerLabelStartPoint instanceof THREE.Vector3 ? ((wall as any).displayInnerLabelStartPoint as THREE.Vector3) : null;
      const displayInnerEndPoint = (wall as any).displayInnerLabelEndPoint instanceof THREE.Vector3 ? ((wall as any).displayInnerLabelEndPoint as THREE.Vector3) : null;
      const displayStartPoint = (wall as any).displayLabelStartPoint instanceof THREE.Vector3 ? ((wall as any).displayLabelStartPoint as THREE.Vector3) : null;
      const displayEndPoint = (wall as any).displayLabelEndPoint instanceof THREE.Vector3 ? ((wall as any).displayLabelEndPoint as THREE.Vector3) : null;

      if (displayInnerStartPoint && displayInnerEndPoint) {
        return {
          start: displayInnerStartPoint.clone(),
          end: displayInnerEndPoint.clone(),
        };
      }

      if (displayStartPoint && displayEndPoint) {
        return {
          start: displayStartPoint.clone(),
          end: displayEndPoint.clone(),
        };
      }

      return {
        start: wall.labelStartPoint.clone(),
        end: wall.labelEndPoint.clone(),
      };
    }

    if (wallData) {
      return {
        start: new THREE.Vector3(wallData.ptC2.x, wallData.ptC2.y, 0),
        end: new THREE.Vector3(wallData.ptD2.x, wallData.ptD2.y, 0),
      };
    }

    return {
      start: wall.startPoint.clone(),
      end: wall.endPoint.clone(),
    };
  }, [wall, wallData]);

  const indicatorPoints = useMemo(() => {
    if (!wall || !labelPoints) return null;

    const applyDockDisplayLength = PreviewWallModel.isPreviewWall(wall) ? (wall as any).applyDockDisplayLength !== false : true;
    const hasInnerDisplay =
      PreviewWallModel.isPreviewWall(wall) &&
      (wall as any).displayInnerLabelStartPoint instanceof THREE.Vector3 &&
      (wall as any).displayInnerLabelEndPoint instanceof THREE.Vector3;

    if (applyDockDisplayLength || hasInnerDisplay) {
      return {
        start: labelPoints.start.clone(),
        end: labelPoints.end.clone(),
      };
    }

    const sourceStart = PreviewWallModel.isPreviewWall(wall) && wall.previewStartPoint ? wall.previewStartPoint : wall.startPoint;
    const sourceEnd = PreviewWallModel.isPreviewWall(wall) && wall.previewEndPoint ? wall.previewEndPoint : wall.endPoint;

    const dir = new THREE.Vector3().subVectors(sourceEnd, sourceStart);
    if (dir.lengthSq() < 1e-9) {
      return {
        start: labelPoints.start.clone(),
        end: labelPoints.end.clone(),
      };
    }

    dir.normalize();

    /**
     * CCW preview uses the opposite visual side for the inner length indicator.
     * Shift the indicator baseline by half depth to the other wall side.
     */
    const offset = new THREE.Vector3(-dir.y, dir.x, 0).multiplyScalar(-wall.depth);

    return {
      start: labelPoints.start.clone().add(offset),
      end: labelPoints.end.clone().add(offset),
    };
  }, [wall, labelPoints]);

  const hideLengthIndicators = useMemo(() => PreviewWallModel.isPreviewWall(wall) && (wall as any).hideLengthIndicators === true, [wall]);

  const displayLength = useMemo(() => {
    if (!labelPoints) return 0;

    return labelPoints.start.distanceTo(labelPoints.end) / UNIT_TO_METER_DIVISOR;
  }, [labelPoints]);

  const displayInnerPolygonOrientation = useMemo(() => {
    if (!PreviewWallModel.isPreviewWall(wall)) return null;

    const orientation = (wall as any).displayInnerPolygonOrientation;

    return typeof orientation === 'number' && Number.isFinite(orientation) && orientation !== 0 ? orientation : null;
  }, [wall]);

  const outerPoints = useMemo(() => {
    if (!wall || !wallData) return null;

    const displayOuterStartPoint =
      PreviewWallModel.isPreviewWall(wall) && (wall as any).displayOuterStartPoint instanceof THREE.Vector3
        ? ((wall as any).displayOuterStartPoint as THREE.Vector3)
        : null;
    const displayOuterEndPoint =
      PreviewWallModel.isPreviewWall(wall) && (wall as any).displayOuterEndPoint instanceof THREE.Vector3
        ? ((wall as any).displayOuterEndPoint as THREE.Vector3)
        : null;
    const displayOuterCenterStartPoint =
      PreviewWallModel.isPreviewWall(wall) && (wall as any).displayOuterCenterStartPoint instanceof THREE.Vector3
        ? ((wall as any).displayOuterCenterStartPoint as THREE.Vector3)
        : null;
    const displayOuterCenterEndPoint =
      PreviewWallModel.isPreviewWall(wall) && (wall as any).displayOuterCenterEndPoint instanceof THREE.Vector3
        ? ((wall as any).displayOuterCenterEndPoint as THREE.Vector3)
        : null;

    if (displayOuterStartPoint && displayOuterEndPoint) {
      const fallbackCenterStartPoint = PreviewWallModel.isPreviewWall(wall) && wall.previewStartPoint ? wall.previewStartPoint.clone() : wall.startPoint.clone();
      const fallbackCenterEndPoint = PreviewWallModel.isPreviewWall(wall) && wall.previewEndPoint ? wall.previewEndPoint.clone() : wall.endPoint.clone();

      return {
        centerStartPoint: displayOuterCenterStartPoint?.clone() ?? fallbackCenterStartPoint,
        centerEndPoint: displayOuterCenterEndPoint?.clone() ?? fallbackCenterEndPoint,
        outerStartPoint: displayOuterStartPoint.clone(),
        outerEndPoint: displayOuterEndPoint.clone(),
        outerLength: displayOuterStartPoint.distanceTo(displayOuterEndPoint) / UNIT_TO_METER_DIVISOR,
      };
    }

    const swapPreviewLengthSides = PreviewWallModel.isPreviewWall(wall) && (wall as any).swapPreviewLengthSides === true;
    const outerStartPoint = swapPreviewLengthSides ? new THREE.Vector3(wallData.ptD2.x, wallData.ptD2.y, 0) : new THREE.Vector3(wallData.ptC.x, wallData.ptC.y, 0);
    const outerEndPoint = swapPreviewLengthSides ? new THREE.Vector3(wallData.ptC2.x, wallData.ptC2.y, 0) : new THREE.Vector3(wallData.ptD.x, wallData.ptD.y, 0);

    const rawCenterStartPoint = PreviewWallModel.isPreviewWall(wall) && wall.previewStartPoint ? wall.previewStartPoint.clone() : wall.startPoint.clone();

    const rawCenterEndPoint = PreviewWallModel.isPreviewWall(wall) && wall.previewEndPoint ? wall.previewEndPoint.clone() : wall.endPoint.clone();
    const centerStartPoint = swapPreviewLengthSides ? rawCenterEndPoint : rawCenterStartPoint;
    const centerEndPoint = swapPreviewLengthSides ? rawCenterStartPoint : rawCenterEndPoint;

    return {
      centerStartPoint,
      centerEndPoint,
      outerStartPoint,
      outerEndPoint,
      outerLength: outerStartPoint.distanceTo(outerEndPoint) / UNIT_TO_METER_DIVISOR,
    };
  }, [wall, wallData]);

  const hatchMaterial = useMemo(
    () =>
      new THREE.ShaderMaterial({
        extensions: { derivatives: true },
        uniforms: {
          uBaseColor: { value: new THREE.Color('#ffffff') },
          uHatchColor: { value: new THREE.Color(meshColor) },
          uSpacing: { value: 1.2 },
          uThickness: { value: 0.1 },
          uOpacity: { value: 0.6 },
        },
        vertexShader: `
          varying vec2 vWorldPos;
          void main() {
            vec4 worldPosition = modelMatrix * vec4(position, 1.0);
            vWorldPos = worldPosition.xy;
            gl_Position = projectionMatrix * viewMatrix * worldPosition;
          }
        `,
        fragmentShader: `
          varying vec2 vWorldPos;
          uniform vec3 uBaseColor;
          uniform vec3 uHatchColor;
          uniform float uSpacing;
          uniform float uThickness;
          uniform float uOpacity;

          void main() {
            float coord = vWorldPos.x - vWorldPos.y;
            float halfSpacing = uSpacing / 2.0;
            float dist = abs(mod(coord + halfSpacing, uSpacing) - halfSpacing);
            float halfThick = uThickness / 2.0;
            float f = length(vec2(dFdx(coord), dFdy(coord)));
            float edge = smoothstep(halfThick - f, halfThick + f, dist);
            vec3 lineColor = mix(uHatchColor, uBaseColor, edge);
            float averageCoverage = uThickness / uSpacing;
            vec3 averageColor = mix(uBaseColor, uHatchColor, averageCoverage);
            float fade = smoothstep(uSpacing * 0.15, uSpacing * 0.6, f);
            gl_FragColor = vec4(mix(lineColor, averageColor, fade), uOpacity);
          }
        `,
        side: THREE.DoubleSide,
        transparent: true,
        depthTest: false,
      }),
    [meshColor],
  );

  const lineZ = 0.05;

  const outlines = useMemo(() => {
    if (!wallData) return null;

    const { ptC, ptD, ptC2, ptD2 } = wallData;

    return {
      sideB: [new THREE.Vector3(ptC.x, ptC.y, lineZ), new THREE.Vector3(ptD.x, ptD.y, lineZ)],
      sideA: [new THREE.Vector3(ptC2.x, ptC2.y, lineZ), new THREE.Vector3(ptD2.x, ptD2.y, lineZ)],
      capStart: [new THREE.Vector3(ptC.x, ptC.y, lineZ), new THREE.Vector3(ptC2.x, ptC2.y, lineZ)],
      capEnd: [new THREE.Vector3(ptD.x, ptD.y, lineZ), new THREE.Vector3(ptD2.x, ptD2.y, lineZ)],
    };
  }, [wallData]);

  if (!wall || !wallData || !outlines || !labelPoints || !indicatorPoints) return null;

  return (
    <group name="group-previewWall" renderOrder={RENDER_ORDERS.wall}>
      <mesh name="previewWall-mesh" geometry={wallData.geometry} position={[0, 0, 0.04]} rotation={[Math.PI / 2, 0, 0]}>
        <primitive object={hatchMaterial} attach="material" />
      </mesh>

      <Line points={outlines.sideB} color={meshColor} lineWidth={3} depthTest={false} />
      <Line points={outlines.sideA} color={meshColor} lineWidth={3} depthTest={false} />
      <Line points={outlines.capStart} color={meshColor} lineWidth={2} depthTest={false} />
      <Line points={outlines.capEnd} color={meshColor} lineWidth={2} depthTest={false} />

      {!hideLengthIndicators &&
        (displayInnerPolygonOrientation !== null ? (
          <InnerWallLengthIndicator
            startPoint={indicatorPoints.start}
            endPoint={indicatorPoints.end}
            showLine={false}
            wallSideALength={displayLength}
            polygonOrientation={displayInnerPolygonOrientation}
          />
        ) : (
          <WallLengthIndicator startPoint={indicatorPoints.start} endPoint={indicatorPoints.end} showLine={false} wallSideALength={displayLength} />
        ))}

      {!hideLengthIndicators && outerPoints && (
        <OuterWallLengthIndicator
          startPoint={outerPoints.centerStartPoint}
          endPoint={outerPoints.centerEndPoint}
          outerStartPoint={outerPoints.outerStartPoint}
          outerEndPoint={outerPoints.outerEndPoint}
          wallSideBLength={outerPoints.outerLength}
          isWallSelected={false}
          forceShow={true}
        />
      )}
    </group>
  );
});

export default PreviewWall;
